import React from 'react';
import useLocalStorage from '../../hooks/useLocalStorage';
import useDebounce from '../../hooks/useDebounce';
import {
  ArrowForwardIos,
  SearchOutlined,
  CloseOutlined,
  ArrowBackIos,
  FastForward,
  FastRewind,
} from '@mui/icons-material';
import {
  Table as TableContainer,
  ArrowPrevNext,
  TableScroll,
  WithoutData,
  SearchInput,
  FilterPage,
  SearchBar,
  PageItems,
  Container,
  RowTable,
  Filters,
  Options,
  Select,
  Option,
  Title,
  Tbody,
  Pages,
  Label,
} from './styles';
import SkeletonElement from '../../skeletons/SkeletonElement';
import useEventListener from '../../hooks/useEventListener';
import TableRows from './TableRows';
import TableHeader from './TableHeader';

const defaultSettings = {
  limit: 10,
  page: 1,
  search: '',
  orderByColumns: '',
  orderByDirection: 'desc',
  searchAdvanced: {},
};

/**
 * @param {{
 *  data: Array
 *  columns: Array
 *  columnNames: Array
 *  columnButtons: Array
 *  columnsModify: Object
 *  advancedFilter: Boolean
 *  advancedFilterOptions: Object
 *  title: String
 *  edit: Function
 *  delete_: Function
 *  read: Function
 *  keyStorage: String
 * }}
 */
export default function Table({
  data = [],
  columns = [],
  columnNames = [],
  columnButtons = [],
  columnsModify = {},
  columnsStyles = {},
  title = '',
  keyStorage,
  advancedFilter = false,
  advancedFilterOptions = {},
}) {
  const [
    {
      limit,
      orderByColumns,
      orderByDirection,
      page,
      search,
      searchAdvanced = columns.reduce(
        (current, next) => ({ ...current, [next]: '' }),
        {},
      ),
    },
    setAppSettings,
  ] = useLocalStorage(keyStorage, defaultSettings);
  const [searchTemp, setSearchTemp] = React.useState('');
  const [loading, setLoading] = React.useState(true);
  const debouncedChange = useDebounce((value) => {
    setAppSettings((settings) => ({
      ...settings,
      search: value,
      page: 1,
    }));
  }, 500);

  const [searchTempAdvanced, setSearchTempAdvanced] =
    React.useState(searchAdvanced);

  const debouncedChangeAdvanced = useDebounce((key, value) => {
    setAppSettings((settings) => ({
      ...settings,
      searchAdvanced: { ...settings.searchAdvanced, [key]: value },
      page: 1,
    }));
  }, 500);

  const functionFilterAdvanced = (data) => {
    const qtde = Object.entries(searchAdvanced).filter(
      ([, value]) => value.length !== 0,
    ).length;
    if (qtde === 0) return data;

    const temp = [];
    Object.entries(searchAdvanced)
      .filter(([, value]) => value !== '')
      .forEach(([key, value]) => {
        const isArray = Array.isArray(value);

        if (!isArray) {
          const noAccent = String(data[key])
            .toString()
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');

          if (
            noAccent.includes(value.trim().toLowerCase()) ||
            String(data[key]).trim().includes(value.trim().toLowerCase())
          )
            temp.push('passar');

          return;
        }

        if (value.includes(data[key])) temp.push('passar');

        return;
      });

    return temp.length === qtde;
  };

  React.useEffect(() => {
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  }, []);

  React.useEffect(() => {
    setSearchTemp(search);
    setSearchTempAdvanced(searchAdvanced);
    // eslint-disable-next-line
  }, []);

  const functionOrder = (item1, item2) => {
    if (orderByColumns === '') return 0;
    switch (orderByDirection) {
      case 'desc':
        if (item2[orderByColumns] > item1[orderByColumns]) return 1;
        if (item2[orderByColumns] < item1[orderByColumns]) return -1;
        return 0;
      case 'asc':
        if (item1[orderByColumns] > item2[orderByColumns]) return 1;
        if (item1[orderByColumns] < item2[orderByColumns]) return -1;
        return 0;
      default:
        return 0;
    }
  };

  const functionFilter = (item) => {
    const noAccent = Object.values(item)
      .toString()
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    const withAccent = Object.values(item).toString().toLowerCase();

    return (
      noAccent.includes(search.trim().toLowerCase()) ||
      withAccent.includes(search.trim().toLowerCase())
    );
  };

  const maxPage =
    Array.isArray(data) &&
    Math.ceil(
      data.filter(functionFilter).filter(functionFilterAdvanced).length / limit,
    );

  useEventListener('keydown', (event) => {
    const keyCode = event.code;
    const arrowRight = 'ArrowRight';
    const arrowLeft = 'ArrowLeft';
    const arrowUp = 'ArrowUp';
    const arrowDown = 'ArrowDown';

    if (
      event.target.nodeName !== 'INPUT' &&
      event.target.nodeName !== 'TEXTAREA' &&
      event.target.nodeName !== 'SELECT'
    ) {
      if (keyCode === arrowRight && page < maxPage) {
        setAppSettings((settings) => ({
          ...settings,
          page: settings.page + 1,
        }));
      }

      if (keyCode === arrowLeft && page !== 1) {
        setAppSettings((settings) => ({
          ...settings,
          page: settings.page - 1,
        }));
      }

      if (keyCode === arrowUp && page < maxPage) {
        setAppSettings((settings) => ({
          ...settings,
          page: maxPage,
        }));
      }

      if (keyCode === arrowDown && page !== 1) {
        setAppSettings((settings) => ({
          ...settings,
          page: 1,
        }));
      }
    }
  });

  return (
    <Container>
      <Filters>
        <Options>
          <Title>{title}</Title>
          <SearchBar>
            <SearchInput
              type="text"
              placeholder="Pesquisar..."
              id="search"
              value={searchTemp}
              onChange={({ target }) => {
                setSearchTemp(target.value);
                debouncedChange(target.value);
              }}
            />
            <label htmlFor="search">
              {search.length > 0 ? (
                <CloseOutlined
                  onClick={() => {
                    setSearchTemp('');
                    setAppSettings((settings) => ({
                      ...settings,
                      search: '',
                    }));
                  }}
                  style={{ cursor: 'pointer', width: '25px', height: '25px' }}
                />
              ) : (
                <SearchOutlined style={{ width: '25px', height: '25px' }} />
              )}
            </label>
          </SearchBar>
        </Options>
        <Pages>
          <PageItems>
            <Select
              value={orderByColumns}
              onChange={({ target }) =>
                setAppSettings((settings) => ({
                  ...settings,
                  orderByColumns: target.value,
                }))
              }
            >
              <Option value="">Ordenar por coluna</Option>
              {columnNames.length
                ? columnNames.map((col, i) => (
                    <Option key={i} value={columns[i]}>
                      {col}
                    </Option>
                  ))
                : columns.map((col, i) => (
                    <Option key={i} value={col}>
                      {col}
                    </Option>
                  ))}
            </Select>
            <Select
              value={orderByDirection}
              onChange={({ target }) =>
                setAppSettings((settings) => ({
                  ...settings,
                  orderByDirection: target.value,
                }))
              }
            >
              <Option value="">Ordenar por tipo</Option>
              <Option value="desc">Decrescente</Option>
              <Option value="asc">Crescente</Option>
            </Select>
          </PageItems>
          <PageItems>
            <FilterPage>
              <RowTable>
                <Label>Linha(s) selecionada(s)</Label>
                <Select
                  value={limit}
                  onChange={({ target: { value } }) =>
                    setAppSettings((settings) => ({
                      ...settings,
                      limit: +value,
                      page: 1,
                    }))
                  }
                >
                  <Option value={10}>10</Option>
                  <Option value={50}>50</Option>
                  <Option value={100}>100</Option>
                </Select>
              </RowTable>
              <RowTable>
                <Label>
                  página {page} de {maxPage}
                </Label>
                <ArrowPrevNext
                  disabled={page === 1}
                  title="Para a primeira página"
                  onClick={() =>
                    setAppSettings((settings) => ({
                      ...settings,
                      page: 1,
                    }))
                  }
                >
                  <FastRewind
                    style={{
                      color: 'var(--color)',
                      width: '25px',
                      height: '25px',
                    }}
                  />
                </ArrowPrevNext>
                <ArrowPrevNext
                  disabled={page === 1}
                  title="Anterior"
                  onClick={() =>
                    setAppSettings((settings) => ({
                      ...settings,
                      page: settings.page - 1,
                    }))
                  }
                >
                  <ArrowBackIos
                    style={{
                      color: 'var(--color)',
                      width: '25px',
                      height: '25px',
                    }}
                  />
                </ArrowPrevNext>
                <ArrowPrevNext
                  disabled={page >= maxPage}
                  title="Próximo"
                  onClick={() =>
                    setAppSettings((settings) => ({
                      ...settings,
                      page: settings.page + 1,
                    }))
                  }
                >
                  <ArrowForwardIos
                    style={{
                      color: 'var(--color)',
                      width: '25px',
                      height: '25px',
                    }}
                  />
                </ArrowPrevNext>
                <ArrowPrevNext
                  disabled={page >= maxPage}
                  title="Para o última página"
                  onClick={() =>
                    setAppSettings((settings) => ({
                      ...settings,
                      page: maxPage,
                    }))
                  }
                >
                  <FastForward
                    style={{
                      color: 'var(--color)',
                      width: '25px',
                      height: '25px',
                    }}
                  />
                </ArrowPrevNext>
              </RowTable>
            </FilterPage>
          </PageItems>
        </Pages>
      </Filters>
      <TableScroll>
        {Array.isArray(data) && !!data?.length ? (
          <TableContainer>
            <TableHeader
              columns={columns}
              columnNames={columnNames}
              columnButtons={columnButtons}
              advancedFilter={advancedFilter}
              searchAdvanced={searchAdvanced}
              advancedFilterOptions={advancedFilterOptions}
              debouncedChangeAdvanced={debouncedChangeAdvanced}
              searchTempAdvanced={searchTempAdvanced}
              setSearchTempAdvanced={setSearchTempAdvanced}
            />
            <Tbody>
              {Array.isArray(data) &&
                data
                  ?.filter(functionFilter)
                  ?.filter(functionFilterAdvanced)
                  .sort(functionOrder)
                  .slice((page - 1) * limit, (page - 1) * limit + limit)
                  .map((data, i) => (
                    <TableRows
                      key={i}
                      columns={columns}
                      columnsStyles={columnsStyles}
                      columnButtons={columnButtons}
                      data={data}
                      columnsModify={columnsModify}
                    />
                  ))}
            </Tbody>
          </TableContainer>
        ) : (
          <>
            {loading &&
              [...Array(limit)].map((_, i) => (
                <SkeletonElement key={i} type="table-data" />
              ))}

            {!loading && (
              <WithoutData>
                <h1 style={{ color: 'var(--color)' }}>
                  Não há dados para serem exibidos neste momento.
                </h1>
              </WithoutData>
            )}
          </>
        )}
      </TableScroll>
    </Container>
  );
}
